import { Table, Thead, Tr, Th, Tbody, Td, Skeleton } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { SkuComponentType } from "__generated__/gql/graphql";
import { SKU_COMPONENT_TYPE_LABELS } from "components/Products/SkuPanel/SkuComponents/utils";
import EmptyPlaceholder, { NA } from "components/Shared/EmptyPlaceholder";
import { formatInteger, makeFormatCurrency } from "utils/formatting";

const formatCurrency = makeFormatCurrency({
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type TableRowProps = {
  componentType: SkuComponentType;
  isLoading?: boolean;
  isUnitsColumnVisible?: boolean;
  price?: Decimal | null;
  quantity?: number | null;
  units?: number | null;
};

function TableRow({
  componentType,
  isLoading,
  isUnitsColumnVisible,
  quantity,
  units,
  price,
}: TableRowProps) {
  return (
    <Tr>
      <Td fontWeight={600} whiteSpace="nowrap">
        {SKU_COMPONENT_TYPE_LABELS[componentType]}
      </Td>

      <Td isNumeric>
        {componentType === SkuComponentType.UNIT ? (
          <EmptyPlaceholder>1</EmptyPlaceholder>
        ) : (
          <Skeleton isLoaded={typeof quantity === "number" || !isLoading}>
            {formatInteger(quantity) ?? <NA />}
          </Skeleton>
        )}
      </Td>

      {isUnitsColumnVisible && (
        <Td isNumeric>
          {componentType === SkuComponentType.UNIT ? (
            <EmptyPlaceholder>1</EmptyPlaceholder>
          ) : (
            <Skeleton isLoaded={typeof units === "number" || !isLoading}>
              {formatInteger(units) ?? <NA />}
            </Skeleton>
          )}
        </Td>
      )}

      <Td isNumeric>
        <Skeleton isLoaded={!!price || !isLoading}>
          {formatCurrency(price) ?? <NA />}
        </Skeleton>
      </Td>
    </Tr>
  );
}

const fragment = gql(/* GraphQL */ `
  fragment ProductLineQuantityPriceVariantsTable on ProductLine {
    id
    innerCaseQuantity
    wholesaleCasePrice
    wholesaleCaseQuantity
    wholesaleCaseQuantityUnits
    wholesaleInnerCasePrice
    wholesaleUnitPrice
  }
`);

type Props = {
  productLine: FragmentType<typeof fragment> | null | undefined;
  isLoading?: boolean;
};

export default function QuantityPriceVariantsTable({
  productLine: productLineFragment,
  isLoading,
}: Props) {
  const productLine = getFragmentData(fragment, productLineFragment);
  return (
    <Table
      variant="labeledValue"
      mt={4}
      sx={{ th: { textAlign: "right", verticalAlign: "bottom" } }}
    >
      <Thead>
        <Tr>
          <Th>&nbsp;</Th>
          <Th>Quantity</Th>
          {!!productLine?.innerCaseQuantity && <Th>Units</Th>}
          <Th>Default Wholesale Price</Th>
        </Tr>
      </Thead>

      <Tbody>
        <TableRow
          componentType={SkuComponentType.UNIT}
          isLoading={isLoading}
          isUnitsColumnVisible={!!productLine?.innerCaseQuantity}
          price={productLine?.wholesaleUnitPrice}
        />

        {!!productLine?.innerCaseQuantity && (
          <TableRow
            componentType={SkuComponentType.INNER}
            isLoading={isLoading}
            isUnitsColumnVisible={!!productLine?.innerCaseQuantity}
            price={productLine?.wholesaleInnerCasePrice}
            quantity={productLine?.innerCaseQuantity}
            units={productLine?.innerCaseQuantity}
          />
        )}

        <TableRow
          componentType={SkuComponentType.CASE}
          isLoading={isLoading}
          isUnitsColumnVisible={!!productLine?.innerCaseQuantity}
          price={productLine?.wholesaleCasePrice}
          quantity={productLine?.wholesaleCaseQuantity}
          units={productLine?.wholesaleCaseQuantityUnits}
        />
      </Tbody>
    </Table>
  );
}
